import { useCallback, useEffect, useMemo, useState } from "react";
import { componentLibrary, type ComponentCategory, type ComponentDefinition } from "../../Pages/pageComponentLibrary";

export type LibraryMode = "essentials" | "all";

export type ComponentLibraryPicker = {
	mode: LibraryMode;
	setMode: (mode: LibraryMode) => void;
	search: string;
	setSearch: (value: string) => void;
	activeCategories: ComponentCategory[];
	toggleCategory: (category: ComponentCategory) => void;
	clearFilters: () => void;
	selection: ComponentDefinition | null;
	setSelection: (definition: ComponentDefinition | null) => void;
	/** Definitions to show given the mode / search / category filters. */
	visibleDefinitions: ComponentDefinition[];
	/** Back to essentials mode, cleared search / filters / selection. */
	reset: () => void;
};

/** Removes diacritics + lowercases for accent-insensitive search. */
function fold(value: string): string {
	return value
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase();
}

type PickerOptions = {
	/** Definitions for which this predicate returns true are never offered. */
	exclude?: (definition: ComponentDefinition) => boolean;
};

/**
 * Shared state for the two-tier component library picker. Owns the mode
 * (« essentiels » vs complete list), the search term, the active category
 * chips and the current selection; derives the visible definition list.
 * Hoisted out of the individual editors so PageEditor, the layout-section
 * editor and the embedded-components editor share one implementation.
 */
export function useComponentLibraryPicker(options?: PickerOptions): ComponentLibraryPicker {
	const exclude = options?.exclude;
	const [mode, setMode] = useState<LibraryMode>("essentials");
	const [search, setSearch] = useState("");
	const [activeCategories, setActiveCategories] = useState<ComponentCategory[]>([]);
	const [selection, setSelection] = useState<ComponentDefinition | null>(null);

	// Offerable = published library minus hidden minus caller-excluded.
	const offerable = useMemo(
		() => componentLibrary.filter((d) => !d.hidden && !(exclude ? exclude(d) : false)),
		// `exclude` is expected stable (defined via useCallback by callers); guard
		// against re-running on every render if it isn't by depending on it directly.
		[exclude]
	);

	const visibleDefinitions = useMemo(() => {
		if (mode === "essentials") {
			return offerable.filter((d) => d.primary);
		}
		const term = fold(search.trim());
		return offerable.filter((d) => {
			if (activeCategories.length > 0 && !activeCategories.includes(d.category)) return false;
			if (!term) return true;
			const haystack = fold([d.title, d.description, d.type, ...(d.tags ?? [])].join(" "));
			return haystack.includes(term);
		});
	}, [offerable, mode, search, activeCategories]);

	const toggleCategory = useCallback((category: ComponentCategory) => {
		setActiveCategories((prev) =>
			prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
		);
	}, []);

	const clearFilters = useCallback(() => {
		setSearch("");
		setActiveCategories([]);
	}, []);

	const reset = useCallback(() => {
		setMode("essentials");
		setSearch("");
		setActiveCategories([]);
		setSelection(null);
	}, []);

	// Keep the selection valid WITHOUT auto-picking: nothing is selected by
	// default (empty preview), and a selection that falls out of the visible set
	// (after a search / category change) is cleared. This lets the modal toggle
	// a card off by clicking it again : an auto-pick would immediately re-select.
	useEffect(() => {
		if (selection && !visibleDefinitions.some((d) => d.type === selection.type)) {
			setSelection(null);
		}
	}, [visibleDefinitions, selection]);

	return {
		mode,
		setMode,
		search,
		setSearch,
		activeCategories,
		toggleCategory,
		clearFilters,
		selection,
		setSelection,
		visibleDefinitions,
		reset
	};
}
